<template>
  <section class="mt-12 w-full">
    <!-- Check if groupedProducts has any entries -->
    <div
      v-if="groupedProducts.length === 0"
      class="text-center text-neutral-400"
    >
      There is currently no availability for {{ blok.headline }}.
    </div>
    <div v-else>
      <!-- Show products for the selected location -->
      <div v-if="groupedProducts[selectedLocation]" class="space-y-4">
        <LazyMarketingNavigationLocationsTabs
          v-if="!blok.disable_location_tabs"
          :blok="blok"
          :uuid="uuid"
          :type="type"
          :grouped-products="groupedProducts"
          :selected-location="selectedLocation"
          @update:selected-location="selectedLocation = $event"
          @location-changed="updateLocation"
        />

        <!-- Products -->
        <div
          :class="[
            'mt-8 grid grid-cols-1 gap-6',
            groupedProducts[selectedLocation].products.length === 1
              ? 'md:grid-cols-1'
              : '',
            groupedProducts[selectedLocation].products.length === 2
              ? 'md:grid-cols-2'
              : '',
            groupedProducts[selectedLocation].products.length >= 3
              ? 'lg:grid-cols-3'
              : '',
          ]"
        >
          <!-- Loop through products for the selected location -->
          <div
            v-for="(product, pIndex) in groupedProducts[selectedLocation]
              .products"
            :key="pIndex"
          >
            <LazyMarketingCardProduct
              :product="product as unknown as ActivityStoryblok"
              :blok="blok"
              :products-count="
                groupedProducts[selectedLocation].products.length
              "
              :location="groupedProducts[selectedLocation].locationName"
              :show-location="blok.disable_location_tabs"
            />
          </div>
        </div>

        <!-- Activity Key -->
        <div v-if="blok.enable_activity_key" class="mt-12 space-y-4">
          <h3 class="text-center font-extrabold text-yellow-400 uppercase">
            Activity Types
          </h3>
          <UCard
            class="w-full"
            :ui="{
              root: 'bg-neutral-900/60 backdrop-blur-lg max-w-prose mx-auto',
              body: 'grid grid-cols-2 p-2 sm:p-4 md:grid-cols-4 gap-2',
            }"
          >
            <div
              class="flex items-center justify-center font-semibold text-neutral-400"
            >
              <UBadge class="bg-transparent">
                <UIcon
                  name="i-lucide-badge-check"
                  class="size-6 shrink-0 text-yellow-400"
                />
              </UBadge>
              <div>Signatures</div>
            </div>
            <div
              class="flex items-center justify-center font-semibold text-neutral-400"
            >
              <UBadge class="bg-transparent">
                <UIcon
                  name="i-lucide-circle-stop"
                  class="size-6 shrink-0 text-yellow-400"
                />
              </UBadge>
              <div>Targets</div>
            </div>
            <div
              class="flex items-center justify-center font-semibold text-neutral-400"
            >
              <UBadge class="bg-transparent">
                <UIcon
                  name="i-lucide-briefcase-business"
                  class="size-6 shrink-0 text-yellow-400"
                />
              </UBadge>
              <div>Corporates</div>
            </div>
            <div
              class="flex items-center justify-center font-semibold text-neutral-400"
            >
              <UBadge class="bg-transparent">
                <UIcon
                  name="i-lucide-puzzle"
                  class="size-6 shrink-0 text-yellow-400"
                />
              </UBadge>
              <div>Kids</div>
            </div>
          </UCard>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import type { PropType } from 'vue';
import type {
  ActivityStoryblok,
  LocationStoryblok,
  GroupStoryblok,
} from '~/types/component-types-sb';
import type { ISbStoryData } from '@storyblok/js';

const route = useRoute();
const router = useRouter();
const { slugify } = useSlugify();

interface GroupedProduct {
  locationName: string;
  locationTown: string;
  locationUUID: string;
  locationSlug: string;
  products: ISbStoryData<ActivityStoryblok>[];
}

const props = defineProps({
  blok: {
    type: Object as PropType<GroupStoryblok>,
    default: () => ({}),
  },
  uuid: {
    type: String,
    default: '',
  },
  type: {
    type: String,
    default: '',
  },
  locations: {
    type: Array as PropType<ISbStoryData<LocationStoryblok>[]>,
    default: () => [],
  },
});

// Create a reactive ref for grouped products by location
const groupedProducts = ref<GroupedProduct[]>([]);

// Create a reactive ref to track the selected location (initially the first location)
const selectedLocation = ref(0);

// Function to group products by their locations
const groupProductsByLocation = (
  locations: ISbStoryData<LocationStoryblok>[],
  products: any[]
): GroupedProduct[] => {
  // Initialize an empty object to store products per location
  const locationProductMap: Record<string, GroupedProduct> = {};

  // Loop through each location and initialize an empty product array for each
  locations.forEach((location: ISbStoryData<LocationStoryblok>) => {
    if (
      !location.uuid ||
      !location.name ||
      !location.slug ||
      !location.content?.town
    )
      return;

    locationProductMap[location.uuid] = {
      locationName: location.name,
      locationTown: location.content.town,
      locationUUID: location.uuid,
      locationSlug: location.slug,
      products: [],
    };
  });

  // Loop through products to associate them with their locations
  products.forEach((product) => {
    // Check if product has location information
    if (product.location) {
      const locationIds = Array.isArray(product.location)
        ? product.location
        : [product.location];

      locationIds.forEach((locId: string) => {
        if (locationProductMap[locId]) {
          locationProductMap[locId].products.push(product);
        }
      });
    }
  });

  return Object.values(locationProductMap).filter(
    (group: GroupedProduct) => group.products.length > 0
  );
};

// Watch for changes in products and locations
watch(
  () => [props.blok.products, props.locations],
  ([newProducts, newLocations]) => {
    if (!newLocations || !newProducts) return;

    // Sort locations alphabetically by name before grouping products
    const sortedLocations = newLocations.slice().sort((a, b) => {
      if (!a.name || !b.name) return 0;
      if (a.name < b.name) return -1;
      if (a.name > b.name) return 1;
      return 0;
    });

    // Group the products by their locations using the sorted locations
    groupedProducts.value = groupProductsByLocation(
      sortedLocations as unknown as ISbStoryData<LocationStoryblok>[],
      newProducts as unknown as ISbStoryData<ActivityStoryblok>[]
    );

    // Reset selected location to the first group if grouped products change
    selectedLocation.value = 0;
  },
  { immediate: true } // Run the function immediately when the component is mounted
);

const updateLocation = (index: number) => {
  selectedLocation.value = index;

  // Convert locationName to lowercase and replace spaces with underscores
  const locationName = slugify(groupedProducts.value[index].locationName);

  // Update the URL with the transformed location name
  router.replace({
    query: {
      ...route.query, // Keep other query params
      location: locationName,
    },
  });
};

// Check if query params exist on page load
onMounted(() => {
  const locationParam = route.query.location;

  if (locationParam) {
    // Find the location that matches the query param (in lowercase and with underscores)
    const foundIndex = groupedProducts.value.findIndex(
      (location) => slugify(location.locationName) === locationParam
    );

    // If a matching location is found, update the selected location
    if (foundIndex !== -1) {
      selectedLocation.value = foundIndex;
    }
  }
});

// Watch for changes in query parameters
watch(
  () => route.query.location,
  (newLocationQuery) => {
    if (newLocationQuery) {
      // Find the location that matches the query param (in lowercase and with underscores)
      const foundIndex = groupedProducts.value.findIndex(
        (location) => slugify(location.locationName) === newLocationQuery
      );

      // If a matching location is found, update the selected location
      if (foundIndex !== -1) {
        selectedLocation.value = foundIndex;
      }
    }
  }
);

// Set Schema
useSchemaOrg([
  defineProduct({
    name: props.blok.headline || '',
    description: props.blok.lead || '',
    image: props.blok.feature_image?.filename
      ? [props.blok.feature_image.filename]
      : [],
    offers:
      props.blok.products
        ?.map((product) => {
          if (!product.content?.price) return null;
          return {
            price: product.content.price,
            priceCurrency: 'GBP',
            availability: 'InStock',
          };
        })
        .filter(Boolean) || [],
  }),
]);
</script>
